import React from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { useCart } from "../context/CartContext";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function CartDrawer({ open, onOpenChange }) {
  const { cart, updateQuantity, removeItem } = useCart();
  const nav = useNavigate();

  const goToCheckout = () => {
    onOpenChange(false);
    nav("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col border-l border-border rounded-none"
        data-testid="cart-drawer"
      >
        <SheetHeader>
          <SheetTitle className="display display-section text-2xl text-left">
            Your Cart <span className="text-muted-foreground text-sm font-normal">({cart?.item_count || 0})</span>
          </SheetTitle>
        </SheetHeader>

        {!cart?.items?.length ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="cart-empty-state">
            <ShoppingBag size={32} className="text-muted-foreground" strokeWidth={1.3} />
            <p className="text-sm text-muted-foreground">Your cart is empty.</p>
            <Button
              onClick={() => { onOpenChange(false); nav("/shop"); }}
              className="btn-gold rounded-none clip-frame-sm px-5"
              data-testid="cart-empty-shop-btn"
            >
              Browse Shop
            </Button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto mt-2 space-y-4 pr-1">
              {cart.items.map((it) => (
                <div key={it.product_id} className="flex gap-3 pb-4 border-b border-border/60" data-testid={`cart-item-${it.product_id}`}>
                  <img src={it.image} alt={it.name} className="w-16 h-16 object-cover clip-frame-sm border border-border shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-tight line-clamp-2">{it.name}</div>
                    <div className="font-mono-2 text-[10px] tracking-widest uppercase text-muted-foreground mt-1">{it.sku}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center border border-border">
                        <button
                          onClick={() => updateQuantity(it.product_id, it.quantity - 1)}
                          data-testid={`cart-decrement-${it.product_id}`}
                          className="w-7 h-7 flex items-center justify-center hover:text-[color:var(--gold)]"
                        >
                          <Minus size={12} />
                        </button>
                        <span className="w-8 text-center text-sm font-mono-2" data-testid={`cart-qty-${it.product_id}`}>{it.quantity}</span>
                        <button
                          onClick={() => updateQuantity(it.product_id, it.quantity + 1)}
                          data-testid={`cart-increment-${it.product_id}`}
                          className="w-7 h-7 flex items-center justify-center hover:text-[color:var(--gold)]"
                        >
                          <Plus size={12} />
                        </button>
                      </div>
                      <div className="text-sm font-mono-2" data-testid={`cart-line-total-${it.product_id}`}>{money(it.line_total)}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => removeItem(it.product_id)}
                    data-testid={`cart-remove-${it.product_id}`}
                    aria-label="Remove item"
                    className="self-start text-muted-foreground hover:text-destructive transition-colors"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between font-mono-2 text-xs uppercase tracking-widest text-muted-foreground">
                <span>Subtotal</span>
                <span className="text-lg text-foreground normal-case tracking-normal" data-testid="cart-subtotal">{money(cart.subtotal)}</span>
              </div>
              <Button
                onClick={goToCheckout}
                className="btn-gold rounded-none clip-frame-sm w-full mt-4 h-12 uppercase text-xs tracking-widest font-mono-2"
                data-testid="cart-checkout-btn"
              >
                Proceed to Checkout
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
